import type { ReactNode } from 'react';

import Icon from '@/components/Icon/Icon';
import Input from '@/components/Input/Input';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { cn } from '@/lib/cn';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  /** URL文字列 */
  url: string | null;
  /** 表示テキスト（省略時はURL自体を表示） */
  displayText?: string | null;
  /** 編集モード時の値変更ハンドラ */
  onChange?: (url: string | null) => void;
  /** 読取時のカスタムコンポーネント */
  readComponent?: ReactNode;
  /** 入力フィールドの幅 */
  inputWidth?: number | string;
  /** 入力フィールドのクラス名 */
  inputClassName?: string;
};

/**
 * Card-based Form / UrlItem
 *
 * URL表示コンポーネント
 * クリック可能なリンクとして表示し、新しいタブで開く
 * Source URL, Download URL, Shared Link 等で使用
 * onChange を指定することで編集モードに対応
 */
export const CFormUrlItem = buildCFormItem<Props>((props: Props) => {
  const displayLabel = props.displayText ?? props.url;

  const defaultReadComponent =
    props.url != null ? (
      <HStack className="gap-1.5 items-start">
        <Icon
          iconName="SquareArrowOutUpRightIcon"
          className="w-5 h-5 shrink-0 mt-0.5"
        />
        <a
          href={props.url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'text-labelSemiLarge font-semibold',
            'hover:underline',
            'focus:outline-none focus:ring-2 focus:ring-primary-fill focus:ring-offset-2',
            'break-all',
          )}
        >
          {displayLabel}
        </a>
      </HStack>
    ) : (
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        -
      </Text>
    );

  // onChange が指定されている場合は編集可能
  const writeComponent =
    props.onChange != null ? (
      <Input
        value={props.url ?? ''}
        onChange={(e) => props.onChange?.(e.target.value || null)}
        width={props.inputWidth}
        className={props.inputClassName}
        disabled={props.readonly}
        type="url"
        placeholder="https://example.com"
      />
    ) : undefined;

  return (
    <CFormItemBase
      {...props}
      readComponent={props.readComponent ?? defaultReadComponent}
      writeComponent={writeComponent}
    />
  );
});
